// src/app/core/services/app-routing.service.ts
import { Injectable, inject } from '@angular/core';
import { UserJourneyStatus } from '@core/models/user.model';
import { ProgressTrackerService } from '@core/services/progress-tracker.service';
import { resolveSubscriptionAccess } from '@modules/subscription/utils/subscription-access.resolver';

@Injectable({
  providedIn: 'root'
})
export class AppRoutingService {
  private progressTracker = inject(ProgressTrackerService);

  /*
   * Retorna a rota correta com base no status da jornada do usuário.
   */
  async getRouteFromStatus(status: UserJourneyStatus): Promise<string> {

    /* =====================================================
     * 1. REGISTRO
     * ===================================================== */
    if (['signup_started', 'terms_pending'].includes(status)) {
      return '/registration/terms';
    }

    if (status === 'user_data_pending') {
      return '/registration/user-data';
    }

    if (status === 'email_confirmation_pending') {
      return '/registration/email-confirmation';
    }

    /* =====================================================
     * 2. ESTADO ATUAL DO USUÁRIO
     * -----------------------------------------------------
     * A partir deste ponto, todas as decisões pertencem à
     * jornada autenticada pós-registration.
     *
     * O Firestore permanece como fonte da verdade.
     * ===================================================== */
    const progress =
      await this.progressTracker.getCurrentProgress();

    const subscriptionAccess = resolveSubscriptionAccess({
      subscriptionStatus:
        progress.subscriptionStatus,
      hasSubscriptionHistory:
        progress.hasSubscriptionHistory,
      subscriptionCycle:
        progress.subscriptionCycle ?? null,
      lastStep:
        progress.lastStep,
    });

    /* =====================================================
     * 3. ASSINATURA
     * -----------------------------------------------------
     * A decisão de acesso e retomada de checkout é centralizada
     * em SubscriptionAccessState.
     * ===================================================== */
    if (
      status === 'subscription_available' ||
      status === 'subscription_pending'
    ) {
      return '/tabs/profile';
    }

    /* =====================================================
    * 4. GUARD-RAIL DE ACESSO
    * -----------------------------------------------------
    * Nenhum estado de avaliação ou treinamento pode
    * ultrapassar este ponto sem acesso vigente.
    *
    * Casos cobertos:
    * - assinatura expirada;
    * - past_due;
    * - estado inconsistente;
    * - ciclo ausente/inválido.
    *
    * A resolução detalhada da ação permanece no Profile.
    * ===================================================== */
    if (!subscriptionAccess.hasAccess) {
      return '/tabs/profile';
    }

    /* =====================================================
     * 5. AVALIAÇÃO
     * -----------------------------------------------------
     * O Profile atua como hub decisório da jornada.
     *
     * Estados de avaliação disponíveis ou em andamento
     * sempre retornam ao Profile.
     *
     * A decisão granular de:
     * - iniciar questionário;
     * - continuar questionário;
     * - retomar fotos/perguntas adicionais;
     *
     * pertence ao ProfileFlowResolver + ProfileComponent.
     * ===================================================== */

    if (
      status === 'evaluation_available' ||
      status === 'evaluation_in_progress'
    ) {
      return '/tabs/profile';
    }

    // Completada
    if (status === 'evaluation_completed') {
      return '/tabs/profile';
    }

    // === TREINAMENTO ===
    if (status === 'training_active') {
      return '/tabs/profile';
    }

    // Fallback seguro
    return '/tabs/profile';
  }
}
